import {v4 as uuid} from 'uuid'

import * as waffle from 'airship/modules/waffle'
import WaffleFlag from 'airship/modules/waffle/flags'
import {VIEWTYPE_ITEMS} from 'airship/page/app-experience-settings/lib/constants'
import {
  BackgroundIAE,
  BackgroundPosition,
  ButtonIAE,
  Color,
  DarkModeType,
  Embedded,
  FallthroughType,
  Typography,
  ViewType,
  ViewTypeItem,
} from 'airship/page/app-experience-settings/types'

export const getViewTypeItem = (viewType: ViewType): ViewTypeItem =>
  VIEWTYPE_ITEMS.find(({id}) => id === viewType) as ViewTypeItem

export enum MenuItemId {
  GENERAL = 'general_menu_section',
  IAA = 'IAA_menu_section',
  AXP = 'AXP_menu_section',
}

export const generateName = (defaultName: string, names: string[]): string => {
  const regex = new RegExp(`^${defaultName} (\\d+)$`)
  // create sorted array of indexes to generate color's name
  const sortedIndexes = names
    .map((name) => {
      const found = name.match(regex)
      return found && found[1] ? parseInt(found[1]) : 0
    })
    .sort((a, b) => a - b)

  if (sortedIndexes.length) {
    return `${defaultName} ${(sortedIndexes.slice(-1)[0] || 0) + 1}`
  }
  return `${defaultName} 1`
}

export const generateTypographyName = (typographies?: Typography[]): string =>
  generateName(
    'Custom text',
    (typographies || []).map(({name}) => name)
  )

export const getDefaultColor = (colorNames?: string[]): Color => {
  return {
    id: uuid(),
    name: generateName('Custom color', colorNames || []),
    lightmode: '#FFFFFF',
    darkmode: '#FFFFFF',
  }
}

export const getDefaultIaxSettingsTypo = (
  typographies?: Typography[],
  defaultName?: string
): Typography => {
  return {
    id: uuid(),
    name: defaultName || generateTypographyName(typographies),
    font: FallthroughType.SANS_SERIF,
    size: 16,
    alignment: 'left',
    style: {
      italic: false,
      bold: false,
      underlined: false,
    },
    color: 'd92c2f04-1580-4d04-8429-7fa7a8feefb6',
  }
}

export const getDefaultIaxSettingsBackground = (
  backgroundNames?: string[],
  defaultName?: string
): BackgroundIAE => {
  return {
    height: {
      default: {value: 100, unit: '%'},
      min: {value: 100, unit: '%'},
      max: {value: 100, unit: '%'},
    },
    width: {
      default: {value: 100, unit: '%'},
      min: {value: 100, unit: '%'},
      max: {value: 100, unit: '%'},
    },
    id: uuid(),
    name: defaultName || generateName('View Style', backgroundNames || []),
    position: BackgroundPosition.Top,
    borderRadius: 0,
  }
}

export const getDefaultIaxSettingsButton = (
  buttonNames?: string[],
  defaultName?: string
): ButtonIAE => {
  return {
    id: uuid(),
    name: defaultName || generateName('Custom button', buttonNames || []),
    typography: 'da2c2f04-1580-4d04-8429-7fa7a8feefc4',
    color: 'd92c2f04-1580-4d04-8429-7fa7a8feefb6',
    border: {
      size: 1,
      color: 'eeb7224e-cca4-4b4f-9109-96e7dd2e3b87',
      radius: 3,
    },
  }
}

export const generateEmebedded = (
  names?: string[],
  name?: string
): Embedded => {
  return {
    id: uuid(),
    name: name || generateName('Custom Embedded', names || []),
    clientId: '',
    description: '',
    height: {
      default: {value: 100, unit: '%'},
      min: {value: 100, unit: '%'},
      max: {value: 100, unit: '%'},
    },
    width: {
      default: {value: 100, unit: '%'},
      min: {value: 100, unit: '%'},
      max: {value: 100, unit: '%'},
    },
  }
}

export const displayColorSquareInIAASettings = waffle.flag_is_active(
  WaffleFlag.IAESettings
)
  ? DarkModeType.LIGHTMODE
  : undefined
